import * as Sentry from "@sentry/react";

/**
 * Telemetry for the inspection flow.
 *
 * The inspection dialog was closing on its own mid-job and taking the tech's
 * unsaved work with it. We had no way to tell how often that happened, which
 * techs it hit, or what triggered it, so the interesting transitions in the
 * inspection lifecycle now leave breadcrumbs, and the two that mean "something
 * went wrong" are reported as Sentry events:
 *
 *  - `inspection.closed_unexpectedly` — the dialog unmounted while it was open
 *    and the tech neither submitted nor closed it. This is the bug itself.
 *  - `inspection.draft_restored` — we handed back work from a previous session,
 *    which means that session ended without submitting. This is the bug's
 *    shadow, and it keeps reporting even if a future regression closes the
 *    dialog by some route we haven't instrumented.
 *
 * Both carry `jobId` and a filled-field count so the volume of lost work is
 * visible, not just the event count.
 */
const CATEGORY = "inspection";

type TelemetryData = Record<string, any>;

const breadcrumb = (message: string, data?: TelemetryData) => {
  Sentry.addBreadcrumb({ category: CATEGORY, message, data });
};

const report = (message: string, level: Sentry.Severity, data?: TelemetryData) => {
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag("area", CATEGORY);
    if (data) scope.setExtras(data);
    Sentry.captureMessage(message);
  });
};

/**
 * Number of leaf values the tech has actually filled in. Used as a rough
 * "how much work is at stake" measure on the events above — an unexpected close
 * with 40 filled fields is a very different incident from one with 2.
 */
export const countFilledFields = (values: any): number => {
  if (values === null || values === undefined || values === "") return 0;
  if (Array.isArray(values)) return values.reduce((acc, item) => acc + countFilledFields(item), 0);
  if (typeof values === "object") {
    return Object.values(values).reduce<number>((acc, item) => acc + countFilledFields(item), 0);
  }
  // `false` is the default for every boolean in the checklist, so it isn't "filled".
  if (values === false) return 0;
  return 1;
};

export const inspectionOpened = (jobId: string) => breadcrumb("inspection.opened", { jobId });

export const inspectionSubmitted = (jobId: string, values: any) =>
  breadcrumb("inspection.submitted", { jobId, filledFields: countFilledFields(values) });

export const inspectionClosedByTech = (jobId: string, values: any) =>
  breadcrumb("inspection.closed_by_tech", { jobId, filledFields: countFilledFields(values) });

export const inspectionDraftSaved = (jobId: string, filledFields: number) =>
  breadcrumb("inspection.draft_saved", { jobId, filledFields });

export const inspectionDraftCleared = (jobId: string, reason: string) =>
  breadcrumb("inspection.draft_cleared", { jobId, reason });

export const inspectionDraftStorageFailed = (jobId: string, operation: string, error: unknown) =>
  report("inspection.draft_storage_failed", "warning" as Sentry.Severity, {
    jobId,
    operation,
    error: error instanceof Error ? error.message : String(error),
  });

/**
 * Work from a previous session was handed back to the tech. Reported rather than
 * breadcrumbed: every one of these is a session that ended with unsaved work.
 */
export const inspectionDraftRestored = (jobId: string, filledFields: number, ageMs: number) =>
  report("inspection.draft_restored", "info" as Sentry.Severity, {
    jobId,
    filledFields,
    ageMinutes: Math.round(ageMs / 60000),
  });

/**
 * The dialog went away while the tech was working in it. Before the draft store
 * existed this was silent and unrecoverable; it is now recoverable, but we still
 * want to know every time it happens so the underlying cause can be chased.
 */
export const inspectionClosedUnexpectedly = (jobId: string, filledFields: number, msOpen: number) =>
  report("inspection.closed_unexpectedly", "warning" as Sentry.Severity, {
    jobId,
    filledFields,
    secondsOpen: Math.round(msOpen / 1000),
  });

/**
 * The job page's query came back with no job while the page was already mounted
 * — a transient GraphQL error, a cache reset, or a refetch in flight. The page
 * used to render `null` here, which unmounted everything below it including an
 * open inspection. It now holds the last known job instead, and drops this
 * breadcrumb so the trail is visible on any event that follows.
 */
export const jobDataVanished = (jobId: string) => breadcrumb("job.data_vanished", { jobId });
